import { type PreParsedSectionItemsLinkProps, type PreParsedSectionLinkProps, useSettings } from '@capsicum/common';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNCWTermsAndConditions } from '../../auth/hooks';
import { useMe } from '../../auth/hooks/useMe';

export const useGetContactUsHereLink = () => {
	const { me } = useMe();

	const CONTACT_US_DETAILS = useMemo(
		() =>
			JSON.stringify({
				email: me.email,
				firstName: me.firstName || '',
				lastName: me.lastName || '',
				userId: me.externalUserId,
				username: me.username
			}),
		[me]
	);

	return useMemo(() => `window.postMessage(JSON.stringify(${CONTACT_US_DETAILS}));`, [CONTACT_US_DETAILS]);
};

export const useSetSectionItemsLinks = (sections: Array<PreParsedSectionLinkProps>) => {
	const [web3TermsAndConditionsLink, setWeb3TermsAndConditions] = useState<string | undefined>(undefined);
	const { settings } = useSettings();
	const { me } = useMe();
	const getNCWTermsAndConditions = useNCWTermsAndConditions();
	const latestAcceptedAgreementLink = me.latestAcceptedAgreementVersion;

	const CONTACT_US_INJECTED_JAVASCRIPT = useGetContactUsHereLink();

	useEffect(() => {
		const fetchWeb3TermsAndConditions = async () => {
			const { link } = await getNCWTermsAndConditions();

			setWeb3TermsAndConditions(link);
		};

		fetchWeb3TermsAndConditions();
	}, []);

	const parseItemLink = useCallback(
		(configServiceParam: PreParsedSectionItemsLinkProps['configServiceParam']) => {
			switch (configServiceParam) {
				case 'termsAndConditions':
					return latestAcceptedAgreementLink;
				case 'web3termsAndConditions':
					return web3TermsAndConditionsLink;
				default:
					return settings?.[configServiceParam] as string;
			}
		},
		[settings, latestAcceptedAgreementLink, web3TermsAndConditionsLink]
	);

	const setItemLink = useCallback(
		(item: PreParsedSectionItemsLinkProps) => {
			const link = parseItemLink(item.configServiceParam);
			const injectedJavaScript =
				item.configServiceParam === 'contactUsHereLink' ? CONTACT_US_INJECTED_JAVASCRIPT : undefined;

			return { ...item, injectedJavaScript, link };
		},
		[CONTACT_US_INJECTED_JAVASCRIPT, parseItemLink, settings]
	);

	return useMemo(
		() =>
			sections.map(section => ({
				items: section.items.map(setItemLink),
				title: section.title
			})),
		[sections, setItemLink]
	);
};
